/**
 * FranchisePerformancePage — `/franchise/performance`.
 *
 * Composite of:
 *   - `GET /v1/franchise/dashboard/franchise/:id` (30-day revenue + commission
 *     + descendants + open alerts + unread bulletins)
 *   - `GET /v1/franchise/revenue/franchise/:id/aggregate` (MTD)
 *   - `GET /v1/franchise/ops/performance-kpis/franchise/:id` (SLA + CSAT
 *     KPI history persisted via the operations slice)
 *
 * Operators record a new SLA / CSAT period inline; the latest snapshot is
 * surfaced as KPI cards alongside the financial figures.
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  aggregateRevenue,
  getFranchiseeDashboard,
  listFranchises,
  listPerformanceKpis,
  recordPerformanceKpi,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

function formatNgn(decimal: string): string {
  const n = Number.parseFloat(decimal);
  if (!Number.isFinite(n)) return `₦${decimal}`;
  return `₦${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function isoMonthStart(): string {
  const d = new Date();
  d.setUTCDate(1);
  return d.toISOString().slice(0, 10);
}

export function FranchisePerformancePage(): JSX.Element {
  const qc = useQueryClient();
  const [franchiseId, setFranchiseId] = useState('');
  const [showRecord, setShowRecord] = useState(false);
  const [recordErr, setRecordErr] = useState<string | null>(null);
  const [kpiForm, setKpiForm] = useState({
    periodStart: isoMonthStart(),
    periodEnd: isoToday(),
    slaOnTimeRate: '95.00',
    csatScore: '4.50',
    slaBreachCount: 0,
    complaintCount: 0,
    note: '',
  });

  const franchises = useQuery({
    queryKey: ['franchise', 'perf', 'franchises'],
    queryFn: () => listFranchises({ pageSize: 100 }),
    retry: false,
  });

  const kpis = useQuery({
    queryKey: ['franchise', 'perf', 'kpis', franchiseId],
    queryFn: () => listPerformanceKpis(franchiseId, { pageSize: 12 }),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  const record = useMutation({
    mutationFn: () =>
      recordPerformanceKpi({
        franchiseId,
        periodStart: kpiForm.periodStart,
        periodEnd: kpiForm.periodEnd,
        slaOnTimeRate: kpiForm.slaOnTimeRate,
        csatScore: kpiForm.csatScore,
        slaBreachCount: kpiForm.slaBreachCount,
        complaintCount: kpiForm.complaintCount,
        ...(kpiForm.note.length > 0 ? { note: kpiForm.note } : {}),
      }),
    onSuccess: () => {
      setShowRecord(false);
      void qc.invalidateQueries({ queryKey: ['franchise', 'perf', 'kpis', franchiseId] });
    },
    onError: (e: Error) => setRecordErr(e.message),
  });

  const dash = useQuery({
    queryKey: ['franchise', 'perf', 'dash', franchiseId],
    queryFn: () => getFranchiseeDashboard(franchiseId),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  const mtd = useQuery({
    queryKey: ['franchise', 'perf', 'mtd', franchiseId],
    queryFn: () =>
      aggregateRevenue(franchiseId, { fromDate: isoMonthStart(), toDate: isoToday() }),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  const denied =
    (dash.isError && dash.error instanceof ApiError &&
      (dash.error.status === 401 || dash.error.status === 403)) ||
    (mtd.isError && mtd.error instanceof ApiError &&
      (mtd.error.status === 401 || mtd.error.status === 403));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Franchise performance"
        description="30-day revenue, commission, descendants, alerts."
      />

      <div className="bg-white border border-border rounded-xl p-4 flex flex-wrap items-center gap-3">
        <span className="text-xs font-medium text-muted">Franchise</span>
        {franchises.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : (
          <select
            value={franchiseId}
            onChange={(e) => setFranchiseId(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg text-sm flex-1 min-w-[260px]"
          >
            <option value="">Select…</option>
            {franchises.data?.items.map((f) => (
              <option key={f.id} value={f.id}>
                {f.legalName} ({f.franchiseNumber})
              </option>
            ))}
          </select>
        )}
        {franchiseId.length > 0 ? (
          <button
            type="button"
            onClick={() => setShowRecord((v) => !v)}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark"
          >
            {showRecord ? 'Cancel' : 'Record KPI'}
          </button>
        ) : null}
      </div>

      {showRecord && franchiseId.length > 0 ? (
        <div className="bg-white border border-border rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-gray-900">Record SLA + CSAT KPI</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Period start</label>
              <input
                type="date"
                value={kpiForm.periodStart}
                onChange={(e) => setKpiForm({ ...kpiForm, periodStart: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Period end</label>
              <input
                type="date"
                value={kpiForm.periodEnd}
                onChange={(e) => setKpiForm({ ...kpiForm, periodEnd: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">SLA on-time %</label>
              <input
                type="text"
                value={kpiForm.slaOnTimeRate}
                onChange={(e) => setKpiForm({ ...kpiForm, slaOnTimeRate: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">CSAT (0-5)</label>
              <input
                type="text"
                value={kpiForm.csatScore}
                onChange={(e) => setKpiForm({ ...kpiForm, csatScore: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">SLA breaches</label>
              <input
                type="number"
                min="0"
                value={kpiForm.slaBreachCount}
                onChange={(e) =>
                  setKpiForm({ ...kpiForm, slaBreachCount: Number.parseInt(e.target.value, 10) || 0 })
                }
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Complaints</label>
              <input
                type="number"
                min="0"
                value={kpiForm.complaintCount}
                onChange={(e) =>
                  setKpiForm({ ...kpiForm, complaintCount: Number.parseInt(e.target.value, 10) || 0 })
                }
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Note</label>
            <input
              type="text"
              value={kpiForm.note}
              onChange={(e) => setKpiForm({ ...kpiForm, note: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
          </div>
          {recordErr ? <p className="text-sm text-danger">{recordErr}</p> : null}
          <button
            type="button"
            onClick={() => {
              setRecordErr(null);
              record.mutate();
            }}
            disabled={record.isPending}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {record.isPending ? 'Saving…' : 'Save KPI'}
          </button>
        </div>
      ) : null}

      {franchiseId.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          Pick a franchise to view performance.
        </p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">FranchiseDashboard/read</code> required.
        </DeferredNotice>
      ) : dash.isPending || mtd.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <KPICard
              title="Revenue (30d)"
              value={formatNgn(dash.data?.last30DayRevenue ?? '0')}
            />
            <KPICard
              title="Commission (30d)"
              value={formatNgn(dash.data?.last30DayCommission ?? '0')}
            />
            <KPICard
              title="Revenue (MTD)"
              value={formatNgn(mtd.data?.totalRevenue ?? '0')}
              hint={`${mtd.data?.snapshotCount ?? 0} snapshots`}
            />
            <KPICard
              title="Descendants"
              value={String(dash.data?.descendantCount ?? 0)}
              hint="Sub-franchises in tree"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <KPICard
              title="SLA on-time"
              value={kpis.data?.latest ? `${kpis.data.latest.slaOnTimeRate}%` : '—'}
              hint={
                kpis.data?.latest
                  ? `${kpis.data.latest.periodStart} → ${kpis.data.latest.periodEnd}`
                  : 'No KPI snapshot yet'
              }
            />
            <KPICard
              title="CSAT score"
              value={kpis.data?.latest ? `${kpis.data.latest.csatScore} / 5` : '—'}
              hint={
                kpis.data?.latest ? `${kpis.data.latest.complaintCount} complaint(s)` : '—'
              }
            />
            <KPICard
              title="SLA breaches"
              value={String(kpis.data?.latest?.slaBreachCount ?? 0)}
              hint="In latest reporting period"
            />
            <KPICard
              title="KPI history"
              value={String(kpis.data?.items.length ?? 0)}
              hint="Recorded periods"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-border rounded-xl p-4">
              <p className="text-xs font-medium text-muted mb-1">Lifecycle</p>
              {dash.data ? (
                <StatusBadge status={dash.data.franchise.status} />
              ) : (
                <p className="text-sm text-muted">—</p>
              )}
            </div>
            <div className="bg-white border border-border rounded-xl p-4">
              <p className="text-xs font-medium text-muted mb-1">Open inventory alerts</p>
              <p className="text-2xl font-semibold text-gray-900">
                {dash.data?.openInventoryAlerts ?? 0}
              </p>
            </div>
            <div className="bg-white border border-border rounded-xl p-4">
              <p className="text-xs font-medium text-muted mb-1">Unread bulletins</p>
              <p className="text-2xl font-semibold text-gray-900">
                {dash.data?.unreadBulletins ?? 0}
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
